// Shared asset-class classification (Phase 9-UI).
//
// Before this module, crypto identification was re-implemented three
// times with symbol heuristics (a "-USD"/"-USDC" suffix test in
// CryptoScreen, the same test again in DashboardScreen, and a
// hardcoded 21-ticker base set in DayTradesScreen). Those heuristics
// cannot work for forex at all: FX symbols like "EUR/USD" have no
// distinguishing suffix, and USD-quoted pairs collide with the crypto
// suffix test. The durable fix is the server-resolved `assetClass`
// field now carried on Pending/Position/Account rows; the symbol
// heuristics survive here only as a FALLBACK for rows that predate
// the field (or degraded lookups) and must never be extended to
// forex.

export type AssetClass = "equity" | "crypto" | "forex";

// Kept in sync with DayTradesScreen's original CRYPTO_BASES set —
// the known crypto base tickers the platform has traded or listed.
const CRYPTO_BASES = new Set([
  "AAVE",
  "ADA",
  "AVAX",
  "BAT",
  "BCH",
  "BTC",
  "DOGE",
  "DOT",
  "ETH",
  "LINK",
  "LTC",
  "MKR",
  "SHIB",
  "SOL",
  "SUSHI",
  "UNI",
  "USDC",
  "USDT",
  "XTZ",
  "YFI",
  "XRP",
]);

/** Narrow an arbitrary wire string to the known asset classes.
 * Returns undefined for null/absent/unknown values so callers can
 * apply their own fallback (usually the symbol heuristics below). */
export function normalizeAssetClass(
  raw: string | null | undefined,
): AssetClass | undefined {
  if (raw === "equity" || raw === "crypto" || raw === "forex") return raw;
  return undefined;
}

/** Suffix heuristic from CryptoScreen — Coinbase-style "-USD"/"-USDC"
 * product ids. Fallback only. */
export function looksLikeCryptoSymbol(symbol: string): boolean {
  const upper = symbol.toUpperCase();
  return upper.endsWith("-USD") || upper.endsWith("-USDC");
}

/** Base-set heuristic from DayTradesScreen — handles Alpaca-style
 * "BTC/USD" and bare "BTCUSD" forms. Fallback only. */
export function looksLikeCryptoPositionSymbol(symbol: string): boolean {
  const normalized = symbol.replace("/", "").replace("-", "").toUpperCase();
  for (const quote of ["USDC", "USDT", "USD"]) {
    if (normalized.endsWith(quote)) {
      return CRYPTO_BASES.has(normalized.slice(0, -quote.length));
    }
  }
  return false;
}

/** Classify a row that may carry a server-resolved assetClass.
 * Order: explicit field wins; then the crypto symbol heuristics;
 * then "equity" (the legacy default). There is deliberately NO
 * forex symbol heuristic — forex rows are only ever classified by
 * the explicit field, because any FX symbol guess ("EUR/USD"?
 * "EURUSD"?) would misfire on equity tickers or crypto pairs. */
export function assetClassOf(row: {
  assetClass?: string | null;
  sym?: string;
  symbol?: string;
}): AssetClass {
  const explicit = normalizeAssetClass(row.assetClass);
  if (explicit !== undefined) return explicit;
  const symbol = row.sym ?? row.symbol ?? "";
  if (
    symbol !== "" &&
    (looksLikeCryptoSymbol(symbol) || looksLikeCryptoPositionSymbol(symbol))
  ) {
    return "crypto";
  }
  return "equity";
}
